import { Injectable, OnDestroy, TemplateRef, inject, Injector } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environments/environment';
import { ReportingFacade } from './facades/reporting.facade';
import { AppFacade } from './facades/app.facade';
import { RefreshModalComponent } from '@app/shared/components/refresh-modal/refresh-modal.component';
import { GeneralLogoutModalComponent } from '@app/shared/components/general-logout-modal/general-logout-modal.component';

const GENERAL_LOGOUT_GRACE_PERIOD_MS = 10_000;

export interface ToastInfos {
    lever?: string;
    campaign_info?: string;
    client_id?: string;
    client_name?: string;
    type?: string;
}

export type ToastData = {
    subject: string;
    message?: string;
    duration: number;
    isExpandable: boolean;
    type?: 'success' | 'error' | 'warning';
    error_value?: unknown;
    infos?: ToastInfos;
    icon?: string;
    isMercure?: boolean;
};

interface MercurePayload {
    subject?: string;
    type?: string;
    message?: string;
    error_value?: unknown;
    infos?: ToastInfos;
    action?: string;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements OnDestroy {
    private messageService = inject(MessageService);
    private cookieService = inject(CookieService);
    private router = inject(Router);
    private reportingFacade = inject(ReportingFacade);
    private injector = inject(Injector);
    private dialogService = inject(DialogService);
    private translate = inject(TranslateService);

    private eventSource?: EventSource;
    private toastTemplate?: TemplateRef<unknown>;
    private _reloadToastActive = false;
    private reloadDataTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private readonly _clientSetupStatus = new Subject<{ client_id: string; client_name: string; success: boolean }>();
    readonly clientSetupStatus$ = this._clientSetupStatus.asObservable();

    ngOnDestroy(): void {
        this.eventSource?.close();
        if (this.reloadDataTimeoutId) {
            clearTimeout(this.reloadDataTimeoutId);
        }
        this._clientSetupStatus.complete();
    }

    public async connect(user_id: string, toastTemplate?: TemplateRef<unknown>): Promise<void> {
        this.toastTemplate = toastTemplate;

        const token = localStorage.getItem('mercureToken');
        if (!token) return;

        if (environment.production) {
            const url = `${environment.ip_mercure}/.well-known/mercure?topic=${user_id}&authorization=${token}`;
            this.eventSource = new EventSource(url, { withCredentials: true });
        } else {
            document.cookie = `mercureAuthorization=${token}; path=/; SameSite=None; Secure`;
            const cookieSet = await this.setCoockies(token);
            if (cookieSet === 'OK') {
                const url = `${environment.ip_mercure}/.well-known/mercure?topic=${user_id}`;
                this.eventSource = new EventSource(url, { withCredentials: true });
            }
        }

        if (this.eventSource) {
            this.eventSource.onmessage = (event: MessageEvent) => {
                let payload: MercurePayload;
                try {
                    payload = JSON.parse(event.data);
                } catch {
                    payload = { subject: 'Notice', message: event.data };
                }

                if (payload.subject === 'user_data_reload') {
                    this.injector.get(AppFacade).reloadUserData().subscribe();
                    return;
                }

                if (payload.subject === 'RELOAD_CAMPAIGN_DATA_TITLE' && payload.type === 'success') {
                    if (this.reloadDataTimeoutId) {
                        clearTimeout(this.reloadDataTimeoutId);
                        this.reloadDataTimeoutId = null;
                    }

                    this.reportingFacade.endReload(payload.infos?.campaign_info);
                    if (this._reloadToastActive) {
                        this.closeReloadToast();
                        const urlParams = this.router.url.split('/');
                        if (urlParams.includes('adetail')) {
                            const currentCampaignType = urlParams[3];
                            const campaignIdWithQuery = urlParams[4];
                            const currentCampaignId = campaignIdWithQuery ? campaignIdWithQuery.split('?')[0] : null;
                            const isMatchingCampaign =
                                currentCampaignType === payload.infos?.type && currentCampaignId === payload.infos?.campaign_info;

                            if (isMatchingCampaign) {
                                this.dialogService.open(RefreshModalComponent, {
                                    header: `Refresh Campaign Data (${payload.infos?.campaign_info || ''})`,
                                    width: '400px',
                                    modal: true,
                                    closable: false,
                                    data: {
                                        campaign_id: payload.infos?.campaign_info || '',
                                        reload_page: true,
                                    },
                                });
                            }
                        }
                    }
                } else if (payload.subject === 'RELOAD_CAMPAIGN_DATA_TITLE' && payload.type === 'error') {
                    if (this.reloadDataTimeoutId) {
                        clearTimeout(this.reloadDataTimeoutId);
                        this.reloadDataTimeoutId = null;
                    }

                    this.reportingFacade.endReload(payload.infos?.campaign_info);
                    this.closeReloadToast();
                }

                if (payload.subject === 'CREATE_CLIENT_AGENCY_TITLE') {
                    this._clientSetupStatus.next({
                        client_id: payload.infos?.client_id ?? '',
                        client_name: payload.infos?.client_name ?? '',
                        success: payload.type === 'success',
                    });
                }

                if (payload.type === 'success' || payload.type === 'error') {
                    const data: ToastData = {
                        subject: payload.subject ?? '',
                        message: payload.message,
                        duration: 5000,
                        isExpandable: !!(payload.error_value && payload.type === 'error'),
                        type: payload.type,
                        error_value: payload.error_value,
                        infos: payload.infos,
                        isMercure: true,
                    };
                    this.showToast(data, payload.type);
                }
            };

            this.eventSource.onerror = (error) => {
                console.error('EventSource failed:', error);
            };
        }
    }

    public async generalConnect(): Promise<void> {
        const generalToken = `${environment.mercureGenealToken}`;
        const url = `${environment.ip_mercure}/.well-known/mercure?topic=user_general&authorization=${generalToken}`;
        this.eventSource = new EventSource(url, { withCredentials: true });

        if (this.eventSource) {
            this.eventSource.onmessage = (event: MessageEvent) => {
                let payload: MercurePayload;
                try {
                    payload = JSON.parse(event.data);
                } catch {
                    payload = { subject: 'Notice', message: 'No Message' };
                }
                if (payload.action && payload.action === 'logout') {
                    this.handleGeneralLogout();
                }
            };

            this.eventSource.onerror = (err) => {
                console.error('Mercure general connection error:', err);
                this.eventSource?.close();
            };
        }
    }

    private async handleGeneralLogout(): Promise<void> {
        const ref = this.dialogService.open(GeneralLogoutModalComponent, {
            header: this.translate.instant('generalLogout.title'),
            width: '400px',
            modal: true,
            closable: false,
        });

        if (ref) {
            await firstValueFrom(ref.onClose);
        }

        if (this.router.url.startsWith('/forms')) {
            // Navigating away while still on /forms triggers unsavedChangesGuard ->
            // FormsComponent.canDeactivate(), reusing its existing save-draft dialog
            // while the auth token is still valid. If the user cancels or ignores it,
            // force the logout anyway after a grace period so it can't be blocked forever.
            await this.raceWithGracePeriod(this.router.navigateByUrl('/home'), GENERAL_LOGOUT_GRACE_PERIOD_MS);
        }

        localStorage.removeItem('currentTheme');
        localStorage.removeItem('currentClient');
        localStorage.removeItem('currentAdvTheme');
        await this.injector.get(AppFacade).logout();
        window.location.reload();
    }

    private raceWithGracePeriod<T>(promise: Promise<T>, graceMs: number): Promise<T | undefined> {
        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => resolve(undefined), graceMs);
            promise.then((value) => {
                clearTimeout(timeoutId);
                resolve(value);
            });
        });
    }

    public async setCoockies(token: string): Promise<string> {
        return new Promise((resolve) => {
            if (token) {
                // Set a cookie
                this.cookieService.set('mercureAuthorization', token, undefined, '/', undefined, true, 'None');
                const coockieSet = this.cookieService.get('mercureAuthorization');
                if (coockieSet) {
                    resolve('OK');
                }
            }
        });
    }

    public showLoadingToast(data: ToastData): void {
        this._reloadToastActive = true;
        this.messageService.add({ key: 'reload', severity: 'info', sticky: true, data });

        if (this.reloadDataTimeoutId) {
            clearTimeout(this.reloadDataTimeoutId);
        }

        this.reloadDataTimeoutId = setTimeout(
            () => {
                console.warn('Reload data timeout reached (10 minutes) - closing notification');
                this.closeReloadToast();
                this.reportingFacade.endReload();
                this.reloadDataTimeoutId = null;
            },
            10 * 60 * 1000,
        );
    }

    /** Dismisses the loading toast when the reload request is refused (no Mercure event will ever follow). */
    public cancelReloadToast(): void {
        if (this.reloadDataTimeoutId) {
            clearTimeout(this.reloadDataTimeoutId);
            this.reloadDataTimeoutId = null;
        }
        this.closeReloadToast();
    }

    private closeReloadToast(): void {
        if (this._reloadToastActive) {
            this.messageService.clear('reload');
            this._reloadToastActive = false;
        }
    }

    public showSuccessToast(subject: string): void {
        this.showToast({ subject, duration: 4000, isExpandable: false, type: 'success', icon: '✅' }, 'success');
    }

    public showToast(data: ToastData, type: 'success' | 'error' | 'warning'): void {
        this.messageService.add({
            severity: type === 'warning' ? 'warn' : type,
            data,
            life: type === 'success' ? 4000 : 8000,
        });
    }

    public deleteCoockies(): void {
        // Delete the cookie
        this.cookieService.delete('mercureAuthorization');
    }
}
